import type { SocialProofItem } from "@/lib/api";
import { displayAuthorName } from "@/lib/commentLabels";
import { relativeTime, cn } from "@/lib/utils";
import PlatformAvatar from "./PlatformAvatar";

type Props = {
  item: SocialProofItem;
  mode?: "native" | "landing";
};

function displayName(item: SocialProofItem): string {
  if (item.display_handle) return item.display_handle;
  return displayAuthorName(item.author, item.source_kind || item.platform);
}

function IgComment({ item }: { item: SocialProofItem }) {
  const name = displayName(item);
  return (
    <div className="rounded-2xl bg-white px-3 py-3">
      <div className="flex gap-3">
        <PlatformAvatar name={item.author || name} avatarUrl={item.avatar_url} platform="instagram" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] leading-snug text-slate-900">
            <span className="font-semibold">{name.replace(/^@/, "")}</span>{" "}
            <span className="font-normal whitespace-pre-wrap">{item.quote_anon || item.text}</span>
          </p>
          <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-400">
            {item.created_at && <span>{relativeTime(item.created_at)}</span>}
            {item.like_count > 0 && <span>{item.like_count} polubień</span>}
            <span>Odpowiedz</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FbComment({ item }: { item: SocialProofItem }) {
  const name = displayName(item);
  return (
    <div className="rounded-2xl bg-[#f0f2f5] px-3 py-3">
      <div className="flex gap-2.5">
        <PlatformAvatar name={item.author || name} avatarUrl={item.avatar_url} platform="facebook" size="sm" />
        <div className="min-w-0 flex-1">
          <div className="rounded-2xl bg-white px-3 py-2 shadow-sm">
            <div className="text-[13px] font-semibold text-slate-900">{name}</div>
            <p className="mt-0.5 whitespace-pre-wrap text-[13px] leading-snug text-slate-800">
              {item.quote_anon || item.text}
            </p>
          </div>
          <div className="mt-1 flex gap-3 px-1 text-[11px] font-semibold text-slate-500">
            <span>Lubię to</span>
            <span>Odpowiedz</span>
            {item.created_at && (
              <span className="font-normal text-slate-400">{relativeTime(item.created_at)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DmBubble({ item }: { item: SocialProofItem }) {
  const name = displayName(item);
  const isIg = item.source_kind === "dm_ig" || item.platform === "instagram";
  return (
    <div
      className={cn(
        "rounded-2xl px-3 py-3",
        isIg ? "bg-gradient-to-b from-slate-900 to-slate-800" : "bg-[#eef2ff]",
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <PlatformAvatar
          name={item.author || name}
          avatarUrl={item.avatar_url}
          platform={isIg ? "instagram" : "messenger"}
          size="sm"
        />
        <div className={cn("text-[12px] font-semibold", isIg ? "text-white" : "text-slate-800")}>
          {name}
        </div>
      </div>
      <div
        className={cn(
          "max-w-[92%] rounded-2xl rounded-tl-md px-3 py-2 text-[13px] leading-snug whitespace-pre-wrap",
          isIg ? "bg-slate-700 text-white" : "bg-white text-slate-900 shadow-sm",
        )}
      >
        {item.quote_anon || item.text}
      </div>
      {item.created_at && (
        <div className={cn("mt-1.5 text-[10px]", isIg ? "text-slate-400" : "text-slate-500")}>
          {relativeTime(item.created_at)}
        </div>
      )}
    </div>
  );
}

function LandingQuote({ item }: { item: SocialProofItem }) {
  return (
    <blockquote className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <p className="text-[15px] leading-relaxed text-slate-800">
        „{item.quote_anon || item.text}”
      </p>
      <footer className="mt-3 text-xs font-medium text-slate-500">
        {item.source_kind.startsWith("dm_") ? "Czytelnaczka" : displayName(item)}
        {item.proof_label ? ` · ${item.proof_label}` : ""}
      </footer>
    </blockquote>
  );
}

export default function NativeCommentCard({ item, mode = "native" }: Props) {
  if (mode === "landing") return <LandingQuote item={item} />;
  if (item.source_kind === "comment_ig") return <IgComment item={item} />;
  if (item.source_kind === "comment_fb") return <FbComment item={item} />;
  if (item.source_kind === "dm_fb" || item.source_kind === "dm_ig") return <DmBubble item={item} />;
  if (item.platform === "instagram") return <IgComment item={item} />;
  return <FbComment item={item} />;
}
